import { TrendingUp, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const LAST_UPDATED = 'February 20, 2026';

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 text-white">{title}</h2>
      <div className="space-y-4 text-gray-300 leading-relaxed">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 pl-5 list-disc marker:text-blue-500/70">
      {items.map((item, i) => (
        <li key={i} className="text-gray-300">{item}</li>
      ))}
    </ul>
  );
}

export default function Terms() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0c]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="terms-logo-home"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">Trading Pilot <span className="text-blue-500">360</span></span>
            </div>
            <nav className="flex items-center space-x-6">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                data-testid="terms-nav-home"
              >
                {user ? 'Dashboard' : 'Home'}
              </button>
              {!user && (
                <Button
                  onClick={() => navigate('/?login=1')}
                  variant="outline"
                  className="border-white/20 hover:bg-white/10"
                  data-testid="terms-nav-login"
                >
                  Login
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-sm text-gray-500 mb-10">Last updated: {LAST_UPDATED}</p>

          <div className="space-y-4 text-gray-300 leading-relaxed mb-10">
            <p>
              Trading Pilot 360 provides tools for analysing trading signals and simulating trade
              execution.
            </p>
            <p>By using this platform, you agree to the following terms.</p>
          </div>

          <Section title="No financial advice">
            <p>Trading Pilot 360 does not provide financial advice.</p>
            <p>All signals, scores, and analytics are informational only.</p>
            <p>Users are responsible for their own trading decisions.</p>
          </Section>

          <Section title="Paper trading only">
            <p>The platform currently executes simulated trades only.</p>
            <p>No real money trading is performed by the system.</p>
            <p>Future integrations may allow live execution but will require user confirmation.</p>
          </Section>

          <Section title="Risk disclaimer">
            <p>Trading involves risk.</p>
            <p>Past performance does not guarantee future results.</p>
            <p>Users are responsible for managing their own financial risk.</p>
          </Section>

          <Section title="Account responsibility">
            <p>Users are responsible for:</p>
            <BulletList items={[
              'maintaining password security',
              'ensuring accuracy of account information',
              'keeping login credentials confidential',
            ]} />
            <p>We are not responsible for losses caused by unauthorised access.</p>
          </Section>

          <Section title="Service availability">
            <p>We aim to provide reliable service but do not guarantee uninterrupted availability.</p>
            <p>Features may be updated, modified, or discontinued.</p>
          </Section>

          <Section title="Acceptable use">
            <p>Users agree not to:</p>
            <BulletList items={[
              'attempt to reverse engineer the system',
              'attempt to disrupt service operation',
              'use the platform for unlawful purposes',
              'abuse automation features',
            ]} />
          </Section>

          <Section title="Limitation of liability">
            <p>Trading Pilot 360 is not responsible for:</p>
            <BulletList items={[
              'financial losses',
              'missed trading opportunities',
              'technical issues outside reasonable control',
              'decisions made based on platform data',
            ]} />
          </Section>

          <Section title="Changes to terms">
            <p>We may update these terms from time to time.</p>
            <p>Continued use of the platform indicates acceptance of updated terms.</p>
          </Section>

          <Section title="Contact">
            <p>
              <a href="mailto:contact@tradingpilot360.com" className="text-blue-400 hover:text-blue-300">
                contact@tradingpilot360.com
              </a>
            </p>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold">Trading Pilot <span className="text-blue-500">360</span></span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/about" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-about-link">About</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/disclaimer" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-disclaimer-link">Disclaimer</Link>
              <Link to="/refund-policy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Trading Pilot 360. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
